import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Select,
  Tooltip,
} from '@material-ui/core';
import { setSpecificSpecialEventData } from '../../../redux/actions/newOperationHoursActions';
import { formatTime } from '../../../utils/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    root: {
      flexGrow: 1,
      borderBottom: '1px solid',
      borderBottomColor: '#bdbdbd',
      marginTop: 5,
    },
    demo: {
      backgroundColor: '#f5f5f5',
    },
  }),
);

// eslint-disable-next-line import/no-anonymous-default-export
export default function RowDiningRoom({ disableAll, restaurantId = '' }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch: any = useDispatch();
  const restaurantServices = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities,
  );
  const brand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const driveThruService = restaurantServices.find(
    elem => elem.code === 'driveThru',
  );
  const digitalDriveThruService = restaurantServices.find(
    elem => elem.code === 'digitalDriveThru',
  );
  const {
    drTimeOpenMode,
    drTimeCloseMode,
    drIsOpened24HoursMode,
    drIsOpened24Hours,
    dtIsOpened24Hours,
    digitalDtIsOpened24Hours,
    drIsClosed,
    digitalDtIsClosed,
    dtIsClosed,
    drTimeOpen,
    drTimeClose,
    availableTimes,
    is12HFormat,
  } = useSelector((state: RootStateOrAny) => state.NewOperationHoursReducer);

  const [selectOpenSelectedIndex, setSelectOpenSelectedIndex] = useState(0);

  const disabledSelects =
    disableAll || drIsClosed === 'Y' || drIsOpened24Hours === 'Y';

  const getValueSelectOpen = () => {
    if (drIsClosed === 'Y') {
      return '';
    } else if (drIsOpened24Hours === 'Y') {
      return availableTimes[1];
    } else {
      return drTimeOpen ? drTimeOpen : '';
    }
  };

  const getValueSelectClose = () => {
    if (drIsClosed === 'Y') {
      return '';
    } else if (drIsOpened24Hours === 'Y') {
      return '23:59';
    } else {
      return drTimeClose ? drTimeClose : '';
    }
  };

  const onChangeSelectOpen = event => {
    const select = event.target as HTMLSelectElement;
    if (drTimeOpen !== select.value) {
      dispatch(
        setSpecificSpecialEventData({
          drTimeOpen: select.value,
        }),
      );
    }
  };

  const onChangeSelectClose = event => {
    const select = event.target as HTMLSelectElement;
    if (drTimeClose !== select.value) {
      dispatch(
        setSpecificSpecialEventData({
          drTimeClose: select.value,
        }),
      );
    }
  };

  const onChangeCheckbox24hours = e => {
    const checked = e.target.checked;

    const newState: any = {};

    if (checked) {
      newState.drIsOpened24Hours = 'Y';
      newState.drIsClosed = 'N';
      newState.closeToAll = false;

      if (hasOpenDriveThru() && hasOpenDigitalDriveThru()) {
        newState.open24Hours = true;
      }
    } else {
      newState.drIsOpened24Hours = 'N';
      newState.open24Hours = false;
    }

    dispatch(setSpecificSpecialEventData(newState));
  };

  const onChangeCheckboxIsClosed = e => {
    const checked = e.target.checked;

    const newState: any = {};

    if (checked) {
      newState.drIsClosed = 'Y';
      newState.drIsOpened24Hours = 'N';
      newState.open24Hours = false;

      if (hasCloseDriveThru() && hasCloseDigitalDriveThru()) {
        newState.closeToAll = true;
      }
    } else {
      newState.drIsClosed = 'N';
      newState.closeToAll = false;
    }

    dispatch(setSpecificSpecialEventData(newState));
  };

  useEffect(() => {
    const index = availableTimes.findIndex(elem => elem === drTimeOpen);
    setSelectOpenSelectedIndex(index);
  }, [drTimeOpen]);

  return (
    <div className={classes.root}>
      <Grid container spacing={0}>
        <Grid item xs={4} md={4}>
          <div className={classes.demo}>
            <List>
              <ListItem>
                <ListItemText
                  primary={t('operationHours.diningRoom')}
                  primaryTypographyProps={{ display: 'inline' }}
                  secondary={!restaurantId ? '*' : ''}
                  secondaryTypographyProps={
                    !restaurantId
                      ? { display: 'inline', color: 'error', variant: 'h5' }
                      : undefined
                  }
                />
              </ListItem>
            </List>
          </div>
        </Grid>
        <Grid item xs={1} md={1}>
          {drIsOpened24HoursMode.includes('read') ? (
            <Checkbox
              style={{ marginTop: 10 }}
              disabled={disableAll || !drIsOpened24HoursMode.includes('write')}
              checked={drIsOpened24Hours === 'Y'}
              onChange={onChangeCheckbox24hours}
            />
          ) : null}
        </Grid>
        <Grid item xs={1} md={1}>
          <Checkbox
            style={{ marginTop: 10 }}
            disabled={disableAll}
            checked={drIsClosed === 'Y'}
            onChange={onChangeCheckboxIsClosed}
          />
        </Grid>
        <Grid item xs={3} md={3}>
          {drTimeOpenMode.includes('read') ? (
            <Tooltip title={''}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel htmlFor="outlined-time-native-simple">
                  {t('operationHours.start')}
                </InputLabel>
                <Select
                  style={
                    disabledSelects || !drTimeOpenMode.includes('write')
                      ? { pointerEvents: 'none', color: 'grey' }
                      : undefined
                  }
                  native
                  value={getValueSelectOpen()}
                  label={t('operationHours.start')}
                  inputProps={{
                    name: 'availableTimes',
                    id: 'outlined-time-native-simple',
                  }}
                  onChange={onChangeSelectOpen}
                  disabled={
                    disabledSelects || !drTimeOpenMode.includes('write')
                  }
                >
                  {availableTimes?.map((elem, index) => (
                    <option key={index} value={elem}>
                      {formatTime(elem, is12HFormat)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          ) : null}
        </Grid>
        <Grid item xs={3} md={3}>
          {drTimeCloseMode.includes('read') ? (
            <Tooltip title={''}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel htmlFor="outlined-time-native-simple">
                  {t('operationHours.close')}
                </InputLabel>
                <Select
                  style={
                    disabledSelects || !drTimeCloseMode.includes('write')
                      ? { pointerEvents: 'none', color: 'grey' }
                      : undefined
                  }
                  native
                  value={getValueSelectClose()}
                  label={t('operationHours.close')}
                  inputProps={{
                    name: 'availableTimes',
                    id: 'outlined-time-native-simple',
                  }}
                  onChange={onChangeSelectClose}
                  disabled={
                    disabledSelects || !drTimeCloseMode.includes('write')
                  }
                >
                  {drIsOpened24Hours === 'Y' ? (
                    <option value={'23:59'}>
                      {formatTime('23:59', is12HFormat)}
                    </option>
                  ) : (
                    availableTimes?.map((elem, index) => (
                      <option key={index} value={elem}>
                        {formatTime(elem, is12HFormat)}
                      </option>
                    ))
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          ) : null}
        </Grid>
      </Grid>
    </div>
  );

  function isDriveThruOpened24Hours() {
    return dtIsOpened24Hours === 'Y';
  }

  function isDriveThruClosed() {
    return dtIsClosed === 'Y';
  }

  function isDigitalDriveThruOpened24Hours() {
    return digitalDtIsOpened24Hours === 'Y';
  }

  function isDigitalDriveThruClosed() {
    return digitalDtIsClosed === 'Y';
  }

  function hasDigitalDriveThru() {
    return brand === 'BK' && digitalDriveThruService?.value;
  }

  function hasDriveThru() {
    return driveThruService?.value;
  }

  function hasOpenDriveThru() {
    return !hasDriveThru() || isDriveThruOpened24Hours();
  }

  function hasOpenDigitalDriveThru() {
    return !hasDigitalDriveThru() || isDigitalDriveThruOpened24Hours();
  }

  function hasCloseDriveThru() {
    return !hasDriveThru() || isDriveThruClosed();
  }

  function hasCloseDigitalDriveThru() {
    return !hasDigitalDriveThru() || isDigitalDriveThruClosed();
  }
}

RowDiningRoom.displayName = 'RowDiningRoom';
